import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Link,
  useNavigate,
  useParams,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import {
  Upload,
  FileText,
  Plane,
  Stamp,
  X,
  ChevronLeft,
  Trash2,
  Layers,
  Car,
  FileSignature,
  Download,
  ArrowRight,
} from "lucide-react";
import { ChevronDown } from "lucide-react";
import { PDFDocument } from "pdf-lib";
import axios from "axios";
import { AuthProvider, useAuth } from "./AuthContext";
import LoginPage from "./LoginPage";
import RegisterPage from "./RegisterPage";
import ForgotPasswordPage from "./ForgotPasswordPage";
import ResetPasswordPage from "./ResetPasswordPage";
import AdminDashboard from "./AdminDashboard";
import AdminCompanyAnalytics from "./AdminCompanyAnalytics";
import TemplatesPage from "./TemplatesPage";
import HistoryPage from "./HistoryPage";
import TranslationRulesPage from "./TranslationRulesPage";
import "./App.css";
import FieldSchemaPage from "./FieldSchemaPage";
import AIPromptHintsPage from "./AIPromptHintsPage";
import TemplateEditor from "./TemplateEditor";
import UserDashboard from "./UserDashboard";
import AppShell from "./AppShell";
import PageHeader from "./PageHeader";
import { UploadScreen, ProcessingScreen } from "./RecognitionUpload";
import {
  ScanPreview,
  DownloadBar,
  Segmented,
  StatusFilterBar,
  FieldPairRow,
} from "./ResultParts";
import { getDocUrl, docTypeMeta as docTypeInfo } from "./docTypes";
import { COUNTRIES } from "./countries";
import {
  CIVIL_DOC_KINDS,
  CIVIL_COLOR,
  isCivilDocType,
  isPagePriced,
  canMergePages,
  withTemplatePair,
  civilDocShort,
  civilDocTitle,
} from "./civilDocs";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL !== undefined
    ? process.env.REACT_APP_API_BASE_URL
    : "http://localhost:5001";

axios.defaults.withCredentials = true;
axios.defaults.xsrfCookieName = "csrf_token";
axios.defaults.xsrfHeaderName = "X-CSRFToken";

const DOCUMENT_TYPES = [
  {
    key: "passport",
    label: "Паспорт",
    icon: <Plane size={20} />,
    color: "#3b82f6",
    description: "Заграничный или внутренний паспорт",
  },
  {
    key: "drivers_license",
    label: "Водительское удостоверение",
    icon: <Car size={20} />,
    color: "#10b981",
    description: "Водительские права любой страны",
  },
  // документы РФ с готовыми шаблонами перевода — по плитке на вид
  ...CIVIL_DOC_KINDS.map(({ key, label, description, Icon }) => ({
    key,
    label,
    icon: <Icon size={20} />,
    color: CIVIL_COLOR,
    description,
  })),
  {
    key: "contract",
    label: "Документ / Договор",
    icon: <FileSignature size={20} />,
    color: "#f59e0b",
    description: "Договор, свидетельство, справка и др.",
  },
];

const LANGUAGES = [
  { code: "ru", name: "Русский" },
  { code: "en", name: "Английский" },
  { code: "uz", name: "Узбекский" },
  { code: "tj", name: "Таджикский" },
  { code: "kz", name: "Казахский" },
  { code: "ky", name: "Кыргызский" },
  { code: "az", name: "Азербайджанский" },
  { code: "tk", name: "Туркменский" },
  { code: "hy", name: "Армянский" },
  { code: "ka", name: "Грузинский" },
  { code: "tr", name: "Турецкий" },
  { code: "de", name: "Немецкий" },
  { code: "fr", name: "Французский" },
  { code: "ar", name: "Арабский" },
  { code: "zh", name: "Китайский" },
  { code: "hi", name: "Хинди" },
  { code: "fa", name: "Персидский (фарси)" },
  { code: "uk", name: "Украинский" },
  { code: "be", name: "Белорусский" },
  { code: "pl", name: "Польский" },
  { code: "ro", name: "Румынский" },
  { code: "bg", name: "Болгарский" },
  { code: "hr", name: "Хорватский" },
  { code: "es", name: "Испанский" },
  { code: "pt", name: "Португальский" },
  { code: "it", name: "Итальянский" },

  { code: "sr", name: "Сербский" },

  { code: "mk", name: "Македонский" },
  { code: "nl", name: "Голландский" },
  { code: "sv", name: "Шведский" },
  { code: "no", name: "Норвежский" },
  { code: "da", name: "Датский" },
  { code: "fi", name: "Финский" },
  { code: "cs", name: "Чешский" },
  { code: "sk", name: "Словацкий" },
  { code: "hu", name: "Венгерский" },
  { code: "el", name: "Греческий" },
  { code: "he", name: "Иврит" },
  { code: "id", name: "Индонезийский" },
  { code: "ms", name: "Малайский" },
  { code: "th", name: "Тайский" },
  { code: "vi", name: "Вьетнамский" },
  { code: "ko", name: "Корейский" },
  { code: "ja", name: "Японский" },
].sort((a, b) => a.name.localeCompare(b.name, "ru"));

const getDocTypeFromPath = (pathname) => {
  if (pathname.startsWith("/dl/")) return "drivers_license";
  if (pathname.startsWith("/contract/")) return "contract";
  return "passport";
};

const Modal = ({ isOpen, onClose, title, children }) => {
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>{title}</h2>
          <button type="button" className="modal-close-btn" aria-label="Закрыть" onClick={onClose}>
            <X size={22} aria-hidden="true" />
          </button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
};

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="auth-page">Загрузка...</div>;
  if (!user) return <Navigate to="/login" />;
  if (adminOnly && !user.is_admin) return <Navigate to="/" />;
  return children;
};

// бейдж только у полей, которые требуют внимания; «распознано» — норма
const FIELD_BADGES = {
  uncertain: { label: "Проверьте", cls: "is-warn" },
  partial: { label: "Частично", cls: "is-soft" },
  not_found: { label: "Не найдено", cls: "is-soft" },
};

const FieldStatus = ({ status }) => {
  const b = FIELD_BADGES[status];
  if (!b) return null;
  return <span className={`res-badge ${b.cls}`}>{b.label}</span>;
};

const PageIndicator = ({ page }) =>
  page ? <span className="res-badge is-soft">Стр. {page}</span> : null;

const CountrySelector = ({ value, onChange, label = "Страна документа", id }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  const selected = COUNTRIES.find((c) => c.code === value);
  const filtered = COUNTRIES.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.code.toLowerCase().includes(search.toLowerCase()),
  );

  const close = () => {
    setIsOpen(false);
    setSearch("");
  };

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) close();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div
      className="rec-field"
      ref={ref}
      onKeyDown={(e) => e.key === "Escape" && close()}
    >
      <span className="rec-label" id={id && `${id}-label`}>
        {label} <span aria-hidden="true">*</span>
      </span>
      <button
        type="button"
        id={id}
        className={`rec-select${selected ? "" : " is-empty"}`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-labelledby={id && `${id}-label ${id}`}
        onClick={() => setIsOpen((v) => !v)}
      >
        {selected ? selected.name : "Выберите страну"}
      </button>

      {isOpen && (
        <div className="rec-pop">
          <input
            autoFocus
            type="text"
            className="rec-pop-search"
            placeholder="Поиск страны"
            aria-label="Поиск страны"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="rec-pop-list" role="listbox" aria-label={label}>
            {filtered.length > 0 ? (
              filtered.map((c) => (
                <button
                  key={c.code}
                  type="button"
                  role="option"
                  aria-selected={value === c.code}
                  className="rec-pop-option"
                  onClick={() => {
                    onChange(c.code);
                    close();
                  }}
                >
                  <span>{c.name}</span>
                  <span className="rec-pop-code">{c.code}</span>
                </button>
              ))
            ) : (
              <p className="rec-pop-option" style={{ cursor: "default" }}>
                Не найдено
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const LanguageSelector = ({
  value,
  onChange,
  label,
  id,
  excludeAuto = false,
  exclude = [],
}) => {
  const options = (
    excludeAuto
      ? LANGUAGES
      : [{ code: "auto", name: "Определить автоматически" }, ...LANGUAGES]
  ).filter((l) => !exclude.includes(l.code));
  return (
    <div className="rec-field">
      <label className="rec-label" htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        className="rec-select"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((l) => (
          <option key={l.code} value={l.code}>
            {l.name}
          </option>
        ))}
      </select>
    </div>
  );
};

const DynamicField = ({
  fieldDef,
  value,
  onChange,
  categoryPath,
  index = null,
  isTranslation = false,
}) => {
  const path =
    index !== null
      ? [categoryPath, index, fieldDef.key]
      : [categoryPath, fieldDef.key];
  const displayName = isTranslation
    ? fieldDef.display_name_ru || fieldDef.key
    : fieldDef.display_name_en || fieldDef.display_name_ru || fieldDef.key;
  const isWrapped =
    value !== null && typeof value === "object" && "data" in value;
  const actualValue = isWrapped ? value.data : value;
  const status = isWrapped ? value.status : undefined;
  const page = isWrapped ? value.page : undefined;

  const isMultiline =
    fieldDef.data_type === "text" ||
    fieldDef.key.includes("full_text") ||
    fieldDef.key.includes("remarks") ||
    fieldDef.key.includes("address");
  const fullWidth =
    isMultiline ||
    fieldDef.key.includes("full_name") ||
    fieldDef.key.includes("authority") ||
    fieldDef.key.includes("place_of_birth") ||
    fieldDef.key.includes("mrz");

  const inputCls = `res-input${status === "uncertain" ? " is-warn" : ""}${
    status === "not_found" && !actualValue ? " is-missing" : ""
  }${fieldDef.key.includes("mrz") ? " is-mono" : ""}`;

  return (
    <label
      className={`editable-field ${fullWidth ? "editable-field--full" : ""}`}
    >
      <span
        className="editable-field-label"
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 4,
        }}
      >
        <span>{displayName}</span>
        {status && <FieldStatus status={status} />}
        {page && <PageIndicator page={page} />}
      </span>
      {isMultiline ? (
        <textarea
          className={inputCls}
          value={actualValue ?? ""}
          onChange={(e) => onChange(path, e.target.value)}
        />
      ) : (
        <input
          className={inputCls}
          type="text"
          value={actualValue ?? ""}
          onChange={(e) => onChange(path, e.target.value)}
        />
      )}
    </label>
  );
};

function computeFieldStatusStats(editableData, fieldSchema) {
  const stats = {
    complete: 0,
    partial: 0,
    uncertain: 0,
    not_found: 0,
    total: 0,
  };
  if (!editableData || !fieldSchema) return stats;

  Object.entries(fieldSchema).forEach(([categoryKey, categorySchema]) => {
    const categoryData = editableData[categoryKey];
    if (!categoryData) return;
    const { fields, is_array } = categorySchema;

    if (is_array && Array.isArray(categoryData)) {
      categoryData.forEach((item) => {
        fields.forEach((fd) => {
          const v = item[fd.key];
          if (v && typeof v === "object" && v.status) {
            stats[v.status]++;
            stats.total++;
          }
        });
      });
    } else {
      fields.forEach((fd) => {
        const v = categoryData[fd.key];
        if (v && typeof v === "object" && v.status) {
          stats[v.status]++;
          stats.total++;
        }
      });
    }
  });

  return stats;
}

const getCategoryMeta = (key) => {
  const map = {
    biographical_page: { eyebrow: "Биостраница", title: "Основные данные" },
    mrz: { eyebrow: "Машиночитаемая зона", title: "MRZ" },
    visas: { eyebrow: "Визы", title: "Найденные визы" },
    stamps: { eyebrow: "Штампы въезда/выезда", title: "Штампы" },
    registration_stamps: {
      eyebrow: "Миграционный учёт",
      title: "РВП / ВНЖ / Регистрация",
    },
    dl_main: {
      eyebrow: "Водительское удостоверение",
      title: "Основные данные ВУ",
    },
    dl_categories: { eyebrow: "Категории ТС", title: "Разрешённые категории" },
    // документы РФ по шаблону (services/civil_documents.py)
    doc_main: { eyebrow: "Документ", title: "Поля документа" },
    subjects: { eyebrow: "Приложение", title: "Итоговые оценки" },
    additional_subjects: { eyebrow: "Приложение", title: "Дополнительные сведения" },
    courses: { eyebrow: "Приложение", title: "Дисциплины, практики, ГИА" },
    notary: { eyebrow: "Заверение", title: "Нотариальное удостоверение" },
    apostille: { eyebrow: "Легализация", title: "Апостиль" },
  };
  return map[key] || { eyebrow: key, title: key };
};

const DynamicCategorySection = ({
  categoryKey,
  categorySchema,
  data,
  translatedData,
  onFieldChange,
  statusFilter = "all",
  // строки списка показываем по переводу: правки пишутся в `${cat}_translation`,
  // иначе отредактированное значение не видно в поле
  preferTranslation = false,
}) => {
  if (!categorySchema || !data) return null;
  const { fields, is_array } = categorySchema;
  const { eyebrow, title } = getCategoryMeta(categoryKey);

  const shouldShow = (v) => {
    if (statusFilter === "all") return true;
    if (v === null || v === undefined) return false;
    if (typeof v !== "object" || !("status" in v)) return true;
    return v.status === statusFilter;
  };

  if (is_array) {
    if (!Array.isArray(data) || data.length === 0) return null;
    return (
      <section className="res-array">
        <header className="res-card-head">
          <p className="res-eyebrow">{eyebrow}</p>
          <h2 className="res-card-title">{title}</h2>
        </header>
        {data.map((item, idx) => {
          const hasMatch =
            statusFilter === "all" ||
            fields.some((f) => item[f.key]?.status === statusFilter);
          if (!hasMatch) return null;
          const pageNum = item.page_number;
          const pageVal =
            typeof pageNum === "object" && pageNum !== null
              ? pageNum.data
              : pageNum;
          return (
            <div key={idx} className="res-card res-array-item">
              <div className="card-header">
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {categoryKey === "visas" && <Plane size={16} />}
                  {categoryKey === "stamps" && <Stamp size={16} />}
                  {categoryKey === "dl_categories" && (
                    <Car size={16} style={{ color: "#10b981" }} />
                  )}
                  <span>
                    {title.replace(/ы$/, "").replace(/а$/, "")} {idx + 1}
                  </span>
                  {pageVal && (
                    <span className="visa-page-tag">Стр. {pageVal}</span>
                  )}
                  {item.category?.data && (
                    <span
                      style={{
                        padding: "2px 10px",
                        borderRadius: 6,
                        background: "rgba(16,185,129,0.15)",
                        color: "#10b981",
                        fontSize: "0.85rem",
                        fontWeight: 700,
                      }}
                    >
                      {item.category.data}
                    </span>
                  )}
                </div>
              </div>
              <div className="card-body">
                <div className="editable-grid">
                  {fields
                    .filter((fd) => shouldShow(item[fd.key]))
                    .map((fd) => (
                      <DynamicField
                        key={fd.key}
                        fieldDef={fd}
                        value={
                          (preferTranslation &&
                            translatedData?.[idx]?.[fd.key]) ||
                          item[fd.key]
                        }
                        onChange={onFieldChange}
                        categoryPath={`${categoryKey}_translation`}
                        index={idx}
                        isTranslation={true}
                      />
                    ))}
                </div>
              </div>
            </div>
          );
        })}
      </section>
    );
  }

  const hasTranslation =
    translatedData && Object.keys(translatedData).length > 0;
  const filtered = fields.filter((fd) => shouldShow(data[fd.key]));
  if (filtered.length === 0) return null;

  return (
    <section className="res-card">
      <header className="res-card-head">
        <p className="res-eyebrow">{eyebrow}</p>
        <h2 className="res-card-title">{title}</h2>
      </header>
      <div>
        {hasTranslation ? (
          <>
            <div className="res-row res-row-head" aria-hidden="true">
              <span>Поле</span>
              <span>Оригинал</span>
              <span>Перевод</span>
            </div>
            {filtered.map((fd) => (
              <FieldPairRow
                key={fd.key}
                fd={fd}
                original={data[fd.key]}
                translated={translatedData[fd.key]}
                categoryKey={categoryKey}
                onFieldChange={onFieldChange}
              />
            ))}
          </>
        ) : (
          <div className="data-grid editable-grid">
            {filtered.map((fd) => (
              <DynamicField
                key={fd.key}
                fieldDef={fd}
                value={data[fd.key]}
                onChange={onFieldChange}
                categoryPath={categoryKey}
                isTranslation={false}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

const VisualTranslationResultView = ({ data, recordId }) => {
  const [downloading, setDownloading] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const pages = data?.pages || [];

  const handleDownloadPagePdf = async (pageNum) => {
    setDownloading(`pdf_${pageNum}`);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/passports/${recordId}/visual-page/${pageNum}/pdf`,
        { responseType: "blob" },
      );

      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `page_${pageNum}.pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Download PDF failed:", e);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadPage = async (pageNum) => {
    setDownloading(pageNum);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/passports/${recordId}/visual-page/${pageNum}/docx`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `page_${pageNum}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Download failed:", e);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadAllPdf = async () => {
    setDownloadingAll("pdf");
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/passports/${recordId}/visual-all-pdf`,
        { responseType: "blob" },
      );

      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `translation_all_pages.pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Download all PDF failed:", e);
    } finally {
      setDownloadingAll(false);
    }
  };

  const handleDownloadAll = async () => {
    setDownloadingAll(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/passports/${recordId}/visual-all-docx`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `translation_all_pages.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Download all failed:", e);
    } finally {
      setDownloadingAll(false);
    }
  };

  const targetLangName =
    LANGUAGES.find((l) => l.code === data?.target_language)?.name ||
    data?.target_language ||
    "перевод";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <DownloadBar title={`Как оригинал · ${pages.length} стр.`}>
        <span className="res-download-meta">→ {targetLangName}</span>
        {pages.length > 1 && (
          <>
            <button
              type="button"
              className="px-btn res-btn-dark res-download-btn"
              onClick={handleDownloadAll}
              disabled={downloadingAll === true}
            >
              <Download size={18} aria-hidden="true" />
              {downloadingAll === true ? "Готовим…" : "Все страницы · DOCX (ZIP)"}
            </button>
            <button
              type="button"
              className="px-btn px-btn-sky"
              onClick={handleDownloadAllPdf}
              disabled={downloadingAll === "pdf"}
            >
              <Download size={18} aria-hidden="true" />
              {downloadingAll === "pdf" ? "Готовим…" : "Все страницы · PDF"}
            </button>
          </>
        )}
      </DownloadBar>

      {pages.map((page) => (
        <section key={page.page} className="res-card res-page">
          <header className="res-page-head">
            <h2 className="res-card-title">Страница {page.page}</h2>
            <button
              type="button"
              className="px-chip"
              onClick={() => handleDownloadPage(page.page)}
              disabled={downloading === page.page}
            >
              <Download size={16} aria-hidden="true" />
              {downloading === page.page ? "Готовим…" : "DOCX"}
            </button>
            <button
              type="button"
              className="px-chip"
              onClick={() => handleDownloadPagePdf(page.page)}
              disabled={downloading === `pdf_${page.page}`}
            >
              <Download size={16} aria-hidden="true" />
              {downloading === `pdf_${page.page}` ? "Готовим…" : "PDF"}
            </button>
          </header>
          <img
            className="res-page-img"
            src={`data:image/png;base64,${page.image_base64}`}
            alt={`Переведённая страница ${page.page}`}
          />
        </section>
      ))}
    </div>
  );
};

const RetypesetResultView = ({ data, recordId }) => {
  const [tree, setTree] = useState(
    () =>
      data?.document_tree || {
        doc_type: data?.document_title || "",
        blocks: [],
      },
  );
  // Последняя сохранённая версия: по ней собирается и скачиваемый файл,
  // и превью перевода.
  const [savedTree, setSavedTree] = useState(tree);
  const [downloading, setDownloading] = useState("");
  const [format, setFormat] = useState("docx");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const sourceLangName =
    LANGUAGES.find((l) => l.code === data?.source_language)?.name ||
    data?.source_language ||
    "оригинал";
  const targetLangName =
    LANGUAGES.find((l) => l.code === data?.target_language)?.name ||
    data?.target_language ||
    "перевод";

  const download = async (fmt) => {
    setDownloading(fmt);
    try {
      // файл собирается из сохранённой версии — несохранённые правки не должны теряться
      if (tree !== savedTree && !(await save())) return;
      const res = await axios.get(
        `${API_BASE_URL}/api/passports/${recordId}/retypeset-docx`,
        { params: { format: fmt }, responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `translation_${recordId}.${fmt}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Download failed:", e);
    } finally {
      setDownloading("");
    }
  };

  const save = async () => {
    setSaving(true);
    setSaved(false);
    const sent = tree;
    try {
      await axios.put(
        `${API_BASE_URL}/api/passports/${recordId}/official-document`,
        sent,
      );
      setSavedTree(sent);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      return true;
    } catch (e) {
      console.error("Save failed:", e);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const setBlockText = (idx, val) =>
    setTree((t) => ({
      ...t,
      blocks: t.blocks.map((b, i) => (i === idx ? { ...b, text: val } : b)),
    }));
  const setListItem = (blockIdx, itemIdx, val) =>
    setTree((t) => ({
      ...t,
      blocks: t.blocks.map((b, i) =>
        i === blockIdx
          ? {
              ...b,
              items: (b.items || []).map((it, j) =>
                j === itemIdx ? val : it,
              ),
            }
          : b,
      ),
    }));
  // Вырезанная из оригинала картинка (QR, герб); "" — печатать текстовую пометку.
  const setBlockImage = (idx, val) =>
    setTree((t) => ({
      ...t,
      blocks: t.blocks.map((b, i) => (i === idx ? { ...b, image: val } : b)),
    }));
  // поле строки бланка: значение на линии или подпись под ней
  const setFormField = (blockIdx, fieldIdx, key, val) =>
    setTree((t) => ({
      ...t,
      blocks: t.blocks.map((b, i) =>
        i === blockIdx
          ? {
              ...b,
              fields: (b.fields || []).map((f, j) =>
                j === fieldIdx ? { ...f, [key]: val } : f,
              ),
            }
          : b,
      ),
    }));
  const setCell = (blockIdx, rowIdx, cellIdx, val) =>
    setTree((t) => ({
      ...t,
      blocks: t.blocks.map((b, i) =>
        i === blockIdx
          ? {
              ...b,
              rows: (b.rows || []).map((row, r) =>
                r === rowIdx
                  ? {
                      ...row,
                      cells: (row.cells || []).map((c, ci) =>
                        ci === cellIdx ? val : c,
                      ),
                    }
                  : row,
              ),
            }
          : b,
      ),
    }));

  // Раскладка оригинала (align/bold/italic) — чтобы правка выглядела как в файле.
  const blockTextStyle = (block) => ({
    textAlign: ["center", "right", "justify"].includes(block.align)
      ? block.align
      : undefined,
    fontWeight: block.bold ? 700 : undefined,
    fontStyle: block.italic ? "italic" : undefined,
  });

  const BLOCK_LABELS = {
    heading: "Заголовок",
    paragraph: "Абзац",
    list: "Список",
    table: "Таблица",
    columns: "Колонки",
    form_line: "Строка бланка",
    stamp: "Печать",
    signature: "Подпись",
    figure: "Рисунок",
  };
  const TEXT_BLOCKS = ["heading", "paragraph", "stamp", "signature", "figure"];
  const FIGURE_JUSTIFY = { center: "center", right: "flex-end" };

  return (
    <>
      <DownloadBar>
        <span className="res-download-meta">
          {sourceLangName} → {targetLangName} · чистый перевод
        </span>
        <button
          type="button"
          className="px-btn res-btn-dark res-download-btn"
          onClick={save}
          disabled={saving}
        >
          {saving ? "Сохранение…" : saved ? "Сохранено" : "Сохранить правки"}
        </button>
        <Segmented
          label="Формат"
          value={format}
          onChange={setFormat}
          options={[
            { key: "docx", label: "DOCX" },
            { key: "pdf", label: "PDF" },
          ]}
        />
        <button
          type="button"
          className="px-btn px-btn-sky"
          onClick={() => download(format)}
          disabled={downloading === format}
        >
          <Download size={20} aria-hidden="true" />
          {downloading === format ? "Готовим…" : "Скачать"}
        </button>
      </DownloadBar>

      <div className="res-layout">
        <ScanPreview
          recordId={recordId}
          translationUrl={`${API_BASE_URL}/api/passports/${recordId}/retypeset-docx?format=pdf`}
          translationVersion={savedTree}
          translationStale={tree !== savedTree}
        />
        <section className="res-fields" aria-label="Перевод">
          <div className="dash-col-head" style={{ flexWrap: "wrap" }}>
            <h2 className="px-section-title" style={{ fontSize: 20 }}>
              Перевод · {targetLangName.toLowerCase()}
            </h2>
            <span className="px-page-sub" style={{ margin: 0 }}>
              Нажмите на блок, чтобы исправить текст · **так** — жирный
            </span>
          </div>
          <div className="res-paper">
            {tree.blocks && tree.blocks.length > 0 ? (
              tree.blocks.map((block, i) => (
                <div key={i} className={`res-blk is-${block.type}`}>
                  <span className="res-blk-type">
                    {BLOCK_LABELS[block.type] || block.type}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    {block.type === "figure" && block.image && (
                      <div
                        className="res-blk-figure"
                        style={{ justifyContent: FIGURE_JUSTIFY[block.align] }}
                      >
                        <img
                          src={`data:${
                            block.image.startsWith("/9j/") ? "image/jpeg" : "image/png"
                          };base64,${block.image}`}
                          alt={block.text || "Рисунок из оригинала"}
                        />
                        <button
                          type="button"
                          className="px-btn px-btn-outline res-blk-figure-btn"
                          onClick={() => setBlockImage(i, "")}
                        >
                          Заменить пометкой
                        </button>
                      </div>
                    )}
                    {TEXT_BLOCKS.includes(block.type) &&
                      !(block.type === "figure" && block.image) && (
                      <textarea
                        className="res-blk-input"
                        aria-label={BLOCK_LABELS[block.type] || block.type}
                        value={block.text || ""}
                        rows={Math.max(1, Math.ceil((block.text || "").length / 80))}
                        style={blockTextStyle(block)}
                        onChange={(e) => setBlockText(i, e.target.value)}
                      />
                    )}
                    {block.type === "list" &&
                      (block.items || []).map((it, j) => (
                        <textarea
                          key={j}
                          className="res-blk-input"
                          aria-label={`Пункт ${j + 1}`}
                          value={it}
                          rows={Math.max(1, Math.ceil((it || "").length / 80))}
                          onChange={(e) => setListItem(i, j, e.target.value)}
                        />
                      ))}
                    {block.type === "form_line" && (
                      <div className="res-form-line">
                        {(block.fields || []).map((f, j) => (
                          <div key={j} className="res-form-field">
                            {f.label && <span className="res-form-label">{f.label}</span>}
                            <textarea
                              className="res-cell-input res-form-value"
                              aria-label={`Значение: ${f.caption || f.label || `поле ${j + 1}`}`}
                              value={f.value || ""}
                              rows={1}
                              onChange={(e) => setFormField(i, j, "value", e.target.value)}
                            />
                            <input
                              className="res-form-caption"
                              aria-label={`Подпись под линией, поле ${j + 1}`}
                              value={f.caption || ""}
                              placeholder="(подпись под линией)"
                              onChange={(e) => setFormField(i, j, "caption", e.target.value)}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                    {(block.type === "table" || block.type === "columns") && (
                      <div className="px-table-wrap">
                        <table
                          className={`res-paper-table${
                            block.type === "columns" || block.style === "fields"
                              ? " is-plain"
                              : ""
                          }`}
                        >
                          <tbody>
                            {(block.rows || []).map((row, ri) => (
                              <tr key={ri}>
                                {(row.cells || []).map((cell, ci) => (
                                  <td key={ci}>
                                    <textarea
                                      className="res-cell-input"
                                      aria-label={`${BLOCK_LABELS[block.type]}: строка ${ri + 1}, колонка ${ci + 1}`}
                                      value={cell}
                                      rows={1}
                                      style={blockTextStyle(block)}
                                      onChange={(e) =>
                                        setCell(i, ri, ci, e.target.value)
                                      }
                                    />
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className="px-page-sub">Структура документа не распознана</p>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

const ContractResultView = ({ data, recordId, onTranslationChange }) => {
  const [translatedText, setTranslatedText] = useState(
    data?.translated_text || "",
  );
  const [downloading, setDownloading] = useState(false);

  if (data?.translation_mode === "retypeset") {
    return <RetypesetResultView data={data} recordId={recordId} />;
  }

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/passports/${recordId}/contract-docx`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `translation_${recordId}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Download failed:", e);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadRelayout = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/passports/${recordId}/relayout-pdf`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `translation_${recordId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Re-layout download failed:", e);
    }
  };

  const sourceLangName =
    LANGUAGES.find((l) => l.code === data?.source_language)?.name ||
    data?.source_language ||
    "оригинал";
  const targetLangName =
    LANGUAGES.find((l) => l.code === data?.target_language)?.name ||
    data?.target_language ||
    "перевод";

  const hasPages = Array.isArray(data?.pages) && data.pages.length > 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
      <DownloadBar>
        <span className="res-download-meta">
          {sourceLangName} → {targetLangName}
        </span>
        {data?.relayout_pdf_key && (
          <button
            type="button"
            className="px-btn res-btn-dark res-download-btn"
            onClick={handleDownloadRelayout}
          >
            <Download size={18} aria-hidden="true" />
            PDF · вид сохранён
          </button>
        )}
        <button
          type="button"
          className={`px-btn px-btn-sky${data?.relayout_pdf_key ? "" : " res-download-btn"}`}
          onClick={handleDownload}
          disabled={downloading}
        >
          <Download size={20} aria-hidden="true" />
          {downloading ? "Готовим…" : "DOCX · текст"}
        </button>
      </DownloadBar>

      <div className="res-text-cols">
        <section className="res-card">
          <header className="res-card-head">
            <p className="res-eyebrow">Оригинал · {sourceLangName}</p>
            <h2 className="res-card-title">Исходный текст</h2>
          </header>
          <div className="res-text">{data?.original_text || "Текст не извлечён"}</div>
        </section>
        <section className="res-card">
          <header className="res-card-head">
            <p className="res-eyebrow">Перевод · {targetLangName}</p>
            <h2 className="res-card-title">Переведённый текст</h2>
          </header>
          <textarea
            className="res-text res-text-edit"
            aria-label="Переведённый текст"
            value={translatedText}
            onChange={(e) => {
              setTranslatedText(e.target.value);
              onTranslationChange && onTranslationChange(e.target.value);
            }}
          />
        </section>
      </div>

      {hasPages && <VisualTranslationResultView data={data} recordId={recordId} />}
    </div>
  );
};

const allOptions = [
  {
    key: "visas",
    label: "Виза",
    icon: <Plane size={18} />,
    color: "#3b82f6",
    forTypes: ["passport"],
  },
  {
    key: "stamps",
    label: "Штамп (въезд/выезд)",
    icon: <Stamp size={18} />,
    color: "#f59e0b",
    forTypes: ["passport"],
  },
  {
    key: "registration_stamps",
    label: "РВП / ВНЖ / Регистрация",
    icon: <Stamp size={18} />,
    color: "#10b981",
    forTypes: ["passport"],
  },
  {
    key: "dl_categories",
    label: "Категория ВУ",
    icon: <Car size={18} />,
    color: "#8b5cf6",
    forTypes: ["drivers_license"],
  },
];

const ManualEntrySection = ({
  fieldSchema,
  onAddItem,
  docType = "passport",
}) => {
  const [selectedType, setSelectedType] = useState(null);
  const [formData, setFormData] = useState({});
  const [isExpanded, setIsExpanded] = useState(false);

  const options = allOptions
    .filter((o) => o.forTypes.includes(docType))
    .filter((o) => fieldSchema?.[o.key]);

  if (options.length === 0) return null;

  const handleSelectType = (key) => {
    setSelectedType(key);
    const schema = fieldSchema?.[key];
    if (!schema) return;
    const init = {};
    schema.fields.forEach((f) => {
      init[f.key] =
        f.key === "page_number" ? "" : { data: "", status: "complete" };
    });
    setFormData(init);
  };

  const handleFieldChange = (key, value, isPlain = false) => {
    setFormData((prev) =>
      isPlain
        ? { ...prev, [key]: value }
        : { ...prev, [key]: { ...prev[key], data: value, status: "complete" } },
    );
  };

  const handleSubmit = () => {
    if (!selectedType) return;
    const pageNum = formData.page_number;
    if (!pageNum || isNaN(parseInt(pageNum)) || parseInt(pageNum) < 1) {
      alert("Укажите корректный номер страницы (число ≥ 1)");
      return;
    }
    const item = { ...formData, page_number: parseInt(pageNum) };
    onAddItem(selectedType, item);
    setSelectedType(null);
    setFormData({});
    setIsExpanded(false);
  };

  const schema = selectedType ? fieldSchema?.[selectedType] : null;

  return (
    <div
      style={{
        marginTop: "2rem",
        border: "2px dashed rgba(255,255,255,0.12)",
        borderRadius: 16,
        overflow: "hidden",
        transition: "all 0.3s",
      }}
    >
      <button
        onClick={() => {
          setIsExpanded(!isExpanded);
          if (isExpanded) {
            setSelectedType(null);
            setFormData({});
          }
        }}
        style={{
          width: "100%",
          padding: "1rem 1.25rem",
          background: isExpanded
            ? "rgba(59,130,246,0.07)"
            : "rgba(255,255,255,0.02)",
          border: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          color: "white",
          transition: "all 0.2s",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              background: "rgba(59,130,246,0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#3b82f6",
            }}
          >
            <FileText size={17} />
          </div>
          <div style={{ textAlign: "left" }}>
            <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>
              Добавить запись вручную
            </div>
            <div
              style={{ fontSize: "0.78rem", color: "#6b7280", marginTop: 2 }}
            >
              Добавьте визу, штамп, регистрацию или категорию ВУ вручную
            </div>
          </div>
        </div>
        <ChevronDown
          size={18}
          style={{
            transform: isExpanded ? "rotate(180deg)" : "none",
            transition: "0.3s",
            color: "#6b7280",
          }}
        />
      </button>

      {isExpanded && (
        <div style={{ padding: "0 1.25rem 1.25rem" }}>
          {!selectedType ? (
            <div>
              <div
                style={{
                  fontSize: "0.82rem",
                  color: "#9ca3af",
                  margin: "0.75rem 0",
                }}
              >
                Что добавить?
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: 10,
                }}
              >
                {options.map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => handleSelectType(opt.key)}
                    style={{
                      padding: "1rem",
                      borderRadius: 12,
                      border: `2px solid ${opt.color}30`,
                      background: `${opt.color}08`,
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 8,
                      transition: "all 0.2s",
                      color: "white",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = `${opt.color}60`;
                      e.currentTarget.style.background = `${opt.color}15`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = `${opt.color}30`;
                      e.currentTarget.style.background = `${opt.color}08`;
                    }}
                  >
                    <div style={{ color: opt.color }}>{opt.icon}</div>
                    <span style={{ fontWeight: 600, fontSize: "0.88rem" }}>
                      {opt.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            schema && (
              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    margin: "0.75rem 0 1rem",
                  }}
                >
                  <button
                    onClick={() => {
                      setSelectedType(null);
                      setFormData({});
                    }}
                    style={{
                      background: "rgba(255,255,255,0.08)",
                      border: "none",
                      borderRadius: 8,
                      color: "white",
                      cursor: "pointer",
                      padding: "5px 10px",
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      fontSize: "0.82rem",
                    }}
                  >
                    <ChevronLeft size={15} /> Назад
                  </button>
                  <span style={{ fontWeight: 600, fontSize: "0.9rem" }}>
                    Добавить:{" "}
                    {options.find((o) => o.key === selectedType)?.label}
                  </span>
                </div>

                <div
                  className="card"
                  style={{ border: "1px solid rgba(255,255,255,0.08)" }}
                >
                  <div className="card-body">
                    <div className="editable-grid">
                      {schema.fields.map((fd) => {
                        const isPageNum = fd.key === "page_number";
                        const isMulti =
                          fd.key.includes("full_text") ||
                          fd.key.includes("remarks") ||
                          fd.key.includes("address");
                        const isFull =
                          isMulti ||
                          fd.key.includes("full_name") ||
                          fd.key.includes("authority");
                        const curVal = isPageNum
                          ? formData[fd.key] || ""
                          : formData[fd.key]?.data || "";
                        return (
                          <label
                            key={fd.key}
                            className={`editable-field ${isFull ? "editable-field--full" : ""}`}
                          >
                            <span className="editable-field-label">
                              {fd.display_name_ru || fd.key}
                              {fd.required && (
                                <span
                                  style={{ color: "#ef4444", marginLeft: 4 }}
                                >
                                  *
                                </span>
                              )}
                            </span>
                            {isMulti ? (
                              <textarea
                                className="editable-input editable-input--multiline"
                                value={curVal}
                                onChange={(e) =>
                                  handleFieldChange(
                                    fd.key,
                                    e.target.value,
                                    isPageNum,
                                  )
                                }
                                placeholder={fd.display_name_ru || ""}
                              />
                            ) : (
                              <input
                                className="editable-input"
                                type={isPageNum ? "number" : "text"}
                                min={isPageNum ? "1" : undefined}
                                value={curVal}
                                onChange={(e) =>
                                  handleFieldChange(
                                    fd.key,
                                    e.target.value,
                                    isPageNum,
                                  )
                                }
                                placeholder={fd.display_name_ru || ""}
                              />
                            )}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    marginTop: "0.85rem",
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    className="secondary-btn"
                    style={{ padding: "0.6rem 1.25rem" }}
                    onClick={() => {
                      setSelectedType(null);
                      setFormData({});
                    }}
                  >
                    Отмена
                  </button>
                  <button
                    className="primary-btn"
                    style={{ padding: "0.6rem 1.75rem", background: "#059669" }}
                    onClick={handleSubmit}
                  >
                    Добавить
                  </button>
                </div>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

function MainDashboard() {
  const { checkAuth, user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [documentType, setDocumentType] = useState("passport");
  const [layoutMode, setLayoutMode] = useState("retypeset");

  useEffect(() => {
    if (id) return;
    const t = searchParams.get("type");
    if (
      t === "passport" ||
      t === "drivers_license" ||
      t === "contract" ||
      isCivilDocType(t)
    ) {
      setDocumentType(t);
    }
  }, [searchParams, id]);
  const [files, setFiles] = useState([]);
  const [file, setFile] = useState(null);
  const [fileCountries, setFileCountries] = useState({});
  const [sameCountryForAll, setSameCountryForAll] = useState(true);
  // несколько фото/сканов одного документа (лист, оборот, апостиль) — одна запись
  const [mergePages, setMergePages] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [targetLanguage, setTargetLanguage] = useState("ru");
  // документы РФ переводятся с русского — по умолчанию на английский
  useEffect(() => {
    if (isCivilDocType(documentType))
      setTargetLanguage((cur) => (cur === "ru" ? "en" : cur));
  }, [documentType]);
  const [sourceLanguage, setSourceLanguage] = useState("auto");
  const [isDragging, setIsDragging] = useState(false);

  const [pageCountInfo, setPageCountInfo] = useState(null);
  const [pageCountLoading, setPageCountLoading] = useState(false);

  const [processing, setProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const [error, setError] = useState(null);
  const [duplicateError, setDuplicateError] = useState(null);

  const [selectedRecordId, setSelectedRecordId] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editableData, setEditableData] = useState(null);
  const [editJson, setEditJson] = useState("");
  const [fieldSchema, setFieldSchema] = useState(null);
  const [currentDocType, setCurrentDocType] = useState("passport");
  const [saving, setSaving] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [fillingTemplate, setFillingTemplate] = useState(false);
  const [templatesError, setTemplatesError] = useState(null);
  const [downloadFormat, setDownloadFormat] = useState("docx");
  const [templateDropdownOpen, setTemplateDropdownOpen] = useState(false);
  const [templateSearch, setTemplateSearch] = useState("");
  const templateDropdownRef = useRef(null);

  const [processedFiles, setProcessedFiles] = useState([]);
  const [fileStatuses, setFileStatuses] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");

  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [mergeQueue, setMergeQueue] = useState([]);
  const [mergeSuccess, setMergeSuccess] = useState(null);

  const deepClone = (d) => JSON.parse(JSON.stringify(d ?? null));

  const syncFromPayload = (payload) => {
    const src = payload || {};
    setEditableData(deepClone(src));
    setEditJson(JSON.stringify(src, null, 2));
    setCurrentDocType(src.document_type || "passport");
  };

  const clearEditing = () => {
    setEditableData(null);
    setEditJson("");
  };

  const handleFieldChange = (path, value) => {
    if (!path || path.length === 0) return;
    setEditableData((prev) => {
      if (!prev) return prev;
      const updated = deepClone(prev);
      let cur = updated;
      for (let i = 0; i < path.length - 1; i++) {
        const k = path[i];
        if (cur[k] === undefined || cur[k] === null)
          cur[k] = typeof path[i + 1] === "number" ? [] : {};
        cur = cur[k];
      }
      cur[path[path.length - 1]] = value;
      setEditJson(JSON.stringify(updated, null, 2));
      return updated;
    });
  };

  const handleAddManualItem = useCallback((categoryKey, newItem) => {
    setEditableData((prev) => {
      if (!prev) return prev;
      const updated = JSON.parse(JSON.stringify(prev));
      if (!Array.isArray(updated[categoryKey])) updated[categoryKey] = [];
      updated[categoryKey].push(newItem);
      setEditJson(JSON.stringify(updated, null, 2));
      return updated;
    });
  }, []);

  const handleTranslationChange = useCallback((newText) => {
    setEditableData((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, translated_text: newText };
      setEditJson(JSON.stringify(updated, null, 2));
      return updated;
    });
  }, []);

  const fetchPageCount = async (singleFile) => {
    if (!singleFile) return;
    setPageCountInfo(null);
    setPageCountLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", singleFile);
      const res = await axios.post(`${API_BASE_URL}/api/count-pages`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPageCountInfo(res.data);
    } catch (e) {
      console.error("Page count failed:", e);
      setPageCountInfo(null);
    } finally {
      setPageCountLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target?.files || e.dataTransfer?.files || []);
    if (!files.length) return;
    const valid = files.filter(
      (f) =>
        [
          "application/pdf",
          "image/jpeg",
          "image/png",
          "application/msword",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ].includes(f.type) ||
        f.name.toLowerCase().endsWith(".doc") ||
        f.name.toLowerCase().endsWith(".docx"),
    );
    if (!valid.length) {
      setError("Поддерживаются PDF, DOCX, DOC, JPG, PNG");
      return;
    }
    if (valid.length === 1) {
      setFile(valid[0]);
      setFiles([]);
      if (isPagePriced(documentType)) {
        fetchPageCount(valid[0]);
      }
    } else {
      setFiles(valid);
      setFile(null);
      setPageCountInfo(null);
    }
    setError(null);
    if (e.target?.value) e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileChange(e);
  };

  // файлы, брошенные на карточку загрузки на главной
  const location = useLocation();
  useEffect(() => {
    const dropped = location.state?.files;
    if (!dropped?.length) return;
    handleFileChange({ target: { files: dropped } });
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const fetchLastResults = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/passports`, {
        params: { page: 1, limit: 10 },
      });
      const items = res.data?.items || [];
      if (items.length > 0) {
        setProcessedFiles(
          items.map((r) => ({
            id: r.id,
            name: r.filename || r.full_name || `ID: ${r.id}`,
            docType: r.document_type || "passport",
          })),
        );
      }
    } catch (e) {
      console.warn("Failed to load results", e);
    }
  }, []);

  useEffect(() => {
    fetchLastResults();
  }, [fetchLastResults]);

  const handleUpload = async () => {
    const filesToProcess = files.length > 0 ? files : file ? [file] : [];
    const uploadDocType = documentType;

    if (!filesToProcess.length) {
      setError("Выберите файл");
      return;
    }

    if (documentType === "passport" || documentType === "drivers_license") {
      if (filesToProcess.length === 1 || sameCountryForAll) {
        if (!selectedCountry) {
          setError("Пожалуйста, выберите страну документа");
          return;
        }
      } else {
        const missing = filesToProcess.findIndex(
          (_, idx) => !fileCountries[idx],
        );
        if (missing !== -1) {
          setError(`Укажите страну для файла: ${filesToProcess[missing].name}`);
          return;
        }
      }
    }

    setProcessing(true);
    setError(null);
    setDuplicateError(null);

    // страницы одного документа уходят одним запросом — бэкенд склеит их в PDF
    const uploads =
      mergePages && filesToProcess.length > 1 && canMergePages(documentType)
        ? [filesToProcess]
        : filesToProcess.map((f) => [f]);
    const uploadName = (group) =>
      group.length > 1 ? `${group[0].name} + ещё ${group.length - 1}` : group[0].name;

    setFileStatuses(
      uploads.map((group) => ({
        name: uploadName(group),
        status: "pending",
        id: null,
        docType: uploadDocType,
        error: null,
      })),
    );

    for (let fileIndex = 0; fileIndex < uploads.length; fileIndex++) {
      const group = uploads[fileIndex];
      const currentFile = { name: uploadName(group) };
      const isLast = fileIndex === uploads.length - 1;

      setUploadProgress(5);
      setStatusMessage(
        uploads.length > 1
          ? `Файл ${fileIndex + 1} из ${uploads.length}: ${currentFile.name}`
          : documentType === "contract"
            ? "Загрузка и перевод документа..."
            : "Загрузка файла...",
      );

      setFileStatuses((prev) =>
        prev.map((fs, i) =>
          i === fileIndex ? { ...fs, status: "processing" } : fs,
        ),
      );

      const formData = new FormData();
      group.forEach((f) => formData.append("file", f));
      formData.append("document_type", uploadDocType);
      const countryForFile =
        sameCountryForAll || filesToProcess.length === 1
          ? selectedCountry
          : fileCountries[fileIndex];

      if (countryForFile) formData.append("country_code", countryForFile);
      if (documentType !== "passport")
        formData.append("target_language", targetLanguage);
      if (documentType === "contract")
        formData.append("source_language", sourceLanguage);
      if (documentType === "contract")
        formData.append("layout_mode", layoutMode);

      let cur = 5;
      const interval = setInterval(() => {
        if (cur < 88) {
          cur += Math.random() * 3 + 1;
          setUploadProgress(Math.round(cur));
        }
        if (cur > 15 && cur < 45)
          setStatusMessage(
            uploads.length > 1
              ? `Файл ${fileIndex + 1}/${uploads.length}: Анализ...`
              : "Анализ документа...",
          );
        else if (cur >= 45 && cur < 75)
          setStatusMessage(
            uploads.length > 1
              ? `Файл ${fileIndex + 1}/${uploads.length}: Извлечение данных...`
              : "Извлечение данных...",
          );
        else if (cur >= 75)
          setStatusMessage(
            uploads.length > 1
              ? `Файл ${fileIndex + 1}/${uploads.length}: Финализация...`
              : documentType === "contract"
                ? "Перевод..."
                : "Финализация...",
          );
      }, 300);

      try {
        const res = await axios.post(`${API_BASE_URL}/api/process`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        clearInterval(interval);
        setUploadProgress(100);
        setStatusMessage(
          uploads.length > 1
            ? `Файл ${fileIndex + 1}/${uploads.length} готов!`
            : "Готово!",
        );

        const recId = res.data.record_id || res.data.id;
        setFileStatuses((prev) =>
          prev.map((fs, i) =>
            i === fileIndex
              ? { ...fs, status: "done", id: recId, docType: uploadDocType }
              : fs,
          ),
        );

        if (recId) {
          setProcessedFiles((prev) => [
            { id: recId, name: currentFile.name, docType: uploadDocType },
            ...prev,
          ]);

          if (isLast) {
            syncFromPayload(res.data);
            if (res.data._schema) setFieldSchema(res.data._schema);
            setCurrentDocType(res.data.document_type || uploadDocType);
            setSelectedRecordId(recId);
            navigate(getDocUrl(uploadDocType, recId));
          }
        }

        if (!isLast) {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      } catch (err) {
        clearInterval(interval);
        setUploadProgress(0);
        setStatusMessage("");

        const errMsg =
          err.response?.data?.error ||
          err.response?.data?.message ||
          "Ошибка обработки";

        setFileStatuses((prev) =>
          prev.map((fs, i) =>
            i === fileIndex ? { ...fs, status: "error", error: errMsg } : fs,
          ),
        );

        if (
          err.response?.status === 409 &&
          err.response?.data?.error === "DuplicateDocument"
        ) {
          setDuplicateError(err.response.data);
        }

        if (uploads.length === 1) {
          if (err.response?.status !== 409) {
            setError(`Файл "${currentFile.name}": ${errMsg}`);
          }
          setTimeout(() => setFileStatuses([]), 10000);
          setProcessing(false);
          return;
        }

        if (!isLast) {
          await new Promise((resolve) => setTimeout(resolve, 500));
          continue;
        }

        setTimeout(() => setFileStatuses([]), 10000);
        setProcessing(false);
        return;
      }
    }

    setFile(null);
    setFiles([]);
    setFileCountries({});
    setSameCountryForAll(true);
    setSelectedCountry("");
    setTimeout(() => checkAuth(false), 500);
    setProcessing(false);
  };

  useEffect(() => {
    if (id) {
      setSelectedRecordId(id);
      const load = async () => {
        setDetailLoading(true);
        try {
          const res = await axios.get(`${API_BASE_URL}/api/passports/${id}`);
          const snap = res.data?.data || {};
          syncFromPayload(snap);
          setCurrentDocType(snap.document_type || "passport");
          if (res.data?._schema) {
            setFieldSchema(res.data._schema);
          } else {
            try {
              const sr = await axios.get(
                `${API_BASE_URL}/api/passports/${id}/schema`,
              );
              setFieldSchema(sr.data.schema);
            } catch (e) {
              console.warn("Schema load failed", e);
            }
          }
        } catch (e) {
          setError("Не удалось загрузить документ");
          navigate("/");
        } finally {
          setDetailLoading(false);
        }
      };
      load();
    } else {
      setSelectedRecordId(null);
      clearEditing();
    }
  }, [id, navigate]);

  const handleSave = useCallback(async () => {
    if (!selectedRecordId) return;
    setSaving(true);
    try {
      await axios.post(
        `${API_BASE_URL}/api/passports/${selectedRecordId}/edit`,
        { data: editJson },
      );
    } catch (e) {
      console.error("Save failed", e);
    } finally {
      setSaving(false);
    }
  }, [selectedRecordId, editJson]);

  useEffect(() => {
    const h = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        if (selectedRecordId && !saving) handleSave();
      }
      if (e.key === "Escape" && selectedRecordId)
        navigate(id ? "/history" : "/recognition");
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [selectedRecordId, saving, handleSave, id, navigate]);

  const handleDelete = async () => {
    if (!selectedRecordId || !window.confirm("Удалить документ?")) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/passports/${selectedRecordId}`);
      setSelectedRecordId(null);
      clearEditing();
      navigate("/history");
    } catch (e) {
      console.error("Delete failed", e);
    }
  };

  const fetchTemplates = useCallback(async () => {
    try {
      let userTpls = [],
        publicTpls = [];
      try {
        const ur = await axios.get(`${API_BASE_URL}/api/user-templates`);
        userTpls = (ur.data || []).map((t) => ({
          id: t.id,
          name: t.label,
          country: t.country_code,
          document_type: t.document_type || "passport",
          isUser: true,
        }));
      } catch (e) {
        console.warn("User templates failed", e);
      }
      try {
        const pr = await axios.get(`${API_BASE_URL}/api/user-templates/public`);
        publicTpls = (pr.data || []).map((t) => ({
          id: t.id,
          name: t.label,
          filename: t.filename || "",
          country: t.country_code,
          document_type: t.document_type || "passport",
          isPublic: true,
        }));
      } catch (e) {
        console.warn("Public templates failed", e);
      }
      setTemplates([...userTpls, ...publicTpls]);
    } catch (e) {
      setTemplatesError("Не удалось загрузить шаблоны");
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    const h = (e) => {
      if (
        templateDropdownRef.current &&
        !templateDropdownRef.current.contains(e.target)
      ) {
        setTemplateDropdownOpen(false);
        setTemplateSearch("");
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const availableTemplates = React.useMemo(() => {
    if (!templates.length) return [];

    const byType = withTemplatePair(
      templates.filter((t) => (t.document_type || "passport") === currentDocType),
    );

    if (byType.length === 0) return [];

    const countryCode = editableData?.country_detection?.country_code
      ?.trim()
      .toUpperCase();
    if (!countryCode) return byType;

    const byCountry = byType.filter(
      (t) => (t.country || "").toUpperCase() === countryCode,
    );

    return byCountry.length > 0 ? byCountry : byType;
  }, [templates, editableData, currentDocType]);

  const documentCountry = editableData?.country_detection?.country_code
    ?.trim()
    .toUpperCase();
  const isForDocumentCountry = (t) =>
    !documentCountry || (t.country || "").toUpperCase() === documentCountry;
  const noCountryTemplate =
    availableTemplates.length > 0 && !availableTemplates.some(isForDocumentCountry);

  // Шаблон чужой страны можно выбрать только руками: подставленный молча,
  // он даёт перевод с неверным языком и текстом
  const pickedTemplateId = React.useRef(null);

  useEffect(() => {
    pickedTemplateId.current = null;
  }, [selectedRecordId]);

  useEffect(() => {
    const selected = availableTemplates.find(
      (t) => String(t.id) === String(selectedTemplateId),
    );
    if (selected && pickedTemplateId.current === String(selected.id)) return;
    // Бэкенд по данным документа знает, какой бланк перед нами (СОР/СОРБ 1998 или
    // 2018 г.) и называет файл шаблона; без подсказки — первый шаблон страны
    const wantedFile = editableData?.template_file;
    const recommended =
      wantedFile &&
      availableTemplates.find(
        (t) => t.filename === wantedFile && isForDocumentCountry(t),
      );
    if (recommended) {
      setSelectedTemplateId(String(recommended.id));
      return;
    }
    if (selected && isForDocumentCountry(selected)) return;
    const first = availableTemplates.find(isForDocumentCountry);
    setSelectedTemplateId(first ? String(first.id) : "");
  }, [availableTemplates, selectedRecordId]);

  const handleFillTemplate = async () => {
    if (!selectedTemplateId || !selectedRecordId) return;
    setFillingTemplate(true);
    setTemplatesError(null);
    try {
      await axios.post(
        `${API_BASE_URL}/api/passports/${selectedRecordId}/edit`,
        { data: editJson },
      );
      // «диплом + приложение» — два шаблона одним файлом: id вида "289+290"
      const [mainId, appendId] = String(selectedTemplateId).split("+");
      const append = appendId ? `&append=${appendId}` : "";
      const res = await axios.post(
        `${API_BASE_URL}/api/templates/${mainId}/fill?format=${downloadFormat}${append}`,
        { record_id: selectedRecordId },
      );
      const raw = atob(res.data.content_base64);
      const bytes = new Uint8Array(raw.length);
      for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
      const blob = new Blob([bytes], { type: res.data.content_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.data.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setTemplatesError(e.response?.data?.error || "Ошибка заполнения шаблона");
    } finally {
      setFillingTemplate(false);
    }
  };

  const handleMerge = async (files) => {
    if (files.length < 2) {
      setError("Выберите минимум 2 файла");
      return;
    }
    try {
      setProcessing(true);
      setStatusMessage("Объединение файлов...");
      const pdf = await PDFDocument.create();
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        setStatusMessage(`Обработка ${i + 1}/${files.length}: ${f.name}`);
        const buf = await f.arrayBuffer();
        if (f.type === "application/pdf") {
          const donor = await PDFDocument.load(buf);
          const pages = await pdf.copyPages(donor, donor.getPageIndices());
          pages.forEach((p) => pdf.addPage(p));
        } else {
          const img =
            f.type === "image/png"
              ? await pdf.embedPng(buf)
              : await pdf.embedJpg(buf);
          const page = pdf.addPage();
          const { width, height } = page.getSize();
          const dims = img.scaleToFit(width - 40, height - 40);
          page.drawImage(img, {
            x: (width - dims.width) / 2,
            y: (height - dims.height) / 2,
            ...dims,
          });
        }
      }
      const pdfBytes = await pdf.save();
      const merged = new File(
        [pdfBytes],
        `${files[0].name.split(".")[0]}_merged.pdf`,
        { type: "application/pdf" },
      );
      setMergeSuccess(merged);
      setStatusMessage("Готово!");
    } catch (e) {
      setError("Ошибка объединения файлов");
    } finally {
      setProcessing(false);
    }
  };

  const finalizeMerge = () => {
    setFile(mergeSuccess);
    setMergeModalOpen(false);
    setMergeQueue([]);
    setMergeSuccess(null);
  };

  const docTypeMeta = DOCUMENT_TYPES.find((d) => d.key === documentType);

  const fileList = files.length > 0 ? files : file ? [file] : [];

  const removeFileAt = (idx) => {
    if (files.length > 0) {
      const updated = files.filter((_, i) => i !== idx);
      if (updated.length === 1) {
        setFile(updated[0]);
        setFiles([]);
        if (isPagePriced(documentType)) fetchPageCount(updated[0]);
      } else if (updated.length === 0) {
        setFiles([]);
      } else {
        setFiles(updated);
      }
      setFileCountries({});
    } else {
      setFile(null);
      setPageCountInfo(null);
      setFileCountries({});
      setSameCountryForAll(true);
    }
  };

  // смена типа не сбрасывает выбранные файлы — только зависящие от типа параметры
  const changeDocumentType = (t) => {
    setDocumentType(t);
    setSelectedCountry("");
    setFileCountries({});
    setPageCountInfo(null);
    if (isPagePriced(t) && fileList.length === 1) fetchPageCount(fileList[0]);
  };

  // Бесплатный документ покрывает любое число страниц — бэкенд тогда не списывает.
  const insufficientFunds =
    isPagePriced(documentType) &&
    fileList.length === 1 &&
    !user?.is_admin &&
    !(user?.company?.free_recognitions > 0) &&
    pageCountInfo != null &&
    pageCountInfo.total_cost > (user?.balance ?? 0);

  const canSubmit =
    fileList.length > 0 && !processing && !pageCountLoading && !insufficientFunds;

  const renderUploadOptions = () => {
    const isMulti = fileList.length > 1;
    const mergeToggle = isMulti && canMergePages(documentType) && (
      <label className="rec-checkbox">
        <input
          type="checkbox"
          checked={mergePages}
          onChange={(e) => setMergePages(e.target.checked)}
        />
        Это страницы одного документа (лист, оборот, апостиль) — один перевод
      </label>
    );
    const countryLabel =
      documentType === "passport" ? "Страна паспорта" : "Страна выдачи прав";

    if (documentType === "passport" || documentType === "drivers_license") {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {isMulti && (
            <label className="rec-checkbox">
              <input
                type="checkbox"
                checked={sameCountryForAll}
                onChange={(e) => {
                  setSameCountryForAll(e.target.checked);
                  if (e.target.checked) setFileCountries({});
                }}
              />
              Одна страна для всех файлов
            </label>
          )}

          <div className="rec-fields">
            {(!isMulti || sameCountryForAll) && (
              <CountrySelector
                id="country"
                value={selectedCountry}
                onChange={setSelectedCountry}
                label={countryLabel}
              />
            )}
            {documentType === "drivers_license" ? (
              <LanguageSelector
                id="target-lang"
                value={targetLanguage}
                onChange={setTargetLanguage}
                label="Язык перевода"
                excludeAuto
              />
            ) : (
              <div className="rec-field">
                <span className="rec-label">Язык перевода</span>
                <span className="rec-select" style={{ display: "flex", alignItems: "center", background: "var(--surface-2)", borderColor: "transparent", cursor: "default" }}>
                  Русский
                </span>
              </div>
            )}
          </div>

          {isMulti && !sameCountryForAll && (
            <div className="rec-per-file">
              <span className="rec-label">Страна для каждого файла</span>
              {fileList.map((f, idx) => (
                <CountrySelector
                  key={`${f.name}-${idx}`}
                  id={`country-${idx}`}
                  value={fileCountries[idx] || ""}
                  onChange={(code) =>
                    setFileCountries((prev) => ({ ...prev, [idx]: code }))
                  }
                  label={f.name}
                />
              ))}
            </div>
          )}
        </div>
      );
    }

    if (documentType === "contract") {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {mergeToggle}
          <div className="rec-langs">
            <LanguageSelector
              id="source-lang"
              value={sourceLanguage}
              onChange={setSourceLanguage}
              label="Язык оригинала"
            />
            <span className="rec-langs-arrow" aria-hidden="true">
              <ArrowRight size={22} />
            </span>
            <LanguageSelector
              id="target-lang"
              value={targetLanguage}
              onChange={setTargetLanguage}
              label="Язык перевода"
              excludeAuto
            />
          </div>
          <div className="rec-field">
            <span className="rec-label" id="layout-mode-label">
              Вид перевода
            </span>
            <div className="rec-modes" role="radiogroup" aria-labelledby="layout-mode-label">
              {[
                {
                  key: "retypeset",
                  title: "Чистый перевод",
                  desc: "Документ набирается заново: печати и подписи — пометками в скобках",
                  recommended: true,
                },
                {
                  key: "visual",
                  title: "Как оригинал",
                  desc: "Перевод поверх исходного вида страниц",
                },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  role="radio"
                  aria-checked={layoutMode === opt.key}
                  className={`rec-mode${layoutMode === opt.key ? " is-active" : ""}`}
                  onClick={() => setLayoutMode(opt.key)}
                >
                  <span className="rec-mode-title">
                    {opt.title}
                    {opt.recommended && <span className="rec-mode-badge">рекомендуем</span>}
                  </span>
                  <span className="rec-mode-desc">{opt.desc}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      );
    }

    if (isCivilDocType(documentType)) {
      // документы РФ: с русского на любой язык; подписи шаблона переводятся под него
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {mergeToggle}
          <div className="rec-fields">
            <LanguageSelector
              id="target-lang"
              value={targetLanguage}
              onChange={setTargetLanguage}
              label="Язык перевода"
              excludeAuto
              exclude={["ru"]}
            />
          </div>
        </div>
      );
    }

    return null;
  };

  const resultMeta = docTypeInfo(currentDocType);
  const ResultIcon = resultMeta.Icon;

  const resultTitle =
    currentDocType === "contract"
      ? editableData?.document_title || "Документ"
      : currentDocType === "drivers_license"
        ? editableData?.dl_main?.surname?.data ||
          editableData?.dl_main?.full_name?.data ||
          "Водительское удостоверение"
        : isCivilDocType(currentDocType)
          ? civilDocTitle(editableData) || civilDocShort(currentDocType)
          : editableData?.biographical_page?.full_name?.data || "Паспорт";

  const resultSubtitle = (() => {
    const code = editableData?.country_detection?.country_code;
    const country = code && (COUNTRIES.find((c) => c.code === code)?.name || code);
    const tgt = editableData?.target_language;
    const lang = tgt && LANGUAGES.find((l) => l.code === tgt)?.name;
    return [
      resultMeta.fullLabel || resultMeta.label,
      country,
      lang && `перевод: ${lang.toLowerCase()}`,
    ]
      .filter(Boolean)
      .join(" · ");
  })();

  const selectedTemplate = templates.find(
    (t) => String(t.id) === String(selectedTemplateId),
  );

  const renderTemplatePicker = () => {
    const q = templateSearch.toLowerCase();
    const filtered = availableTemplates.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        (t.country || "").toLowerCase().includes(q),
    );
    return (
      <div className="res-template" ref={templateDropdownRef}>
        <button
          type="button"
          className={`res-template-btn${selectedTemplate ? "" : " is-empty"}`}
          aria-haspopup="listbox"
          aria-expanded={templateDropdownOpen}
          onClick={() => setTemplateDropdownOpen(!templateDropdownOpen)}
        >
          <span className="res-template-label">Шаблон</span>
          <span className="res-template-name">
            {selectedTemplate?.name ||
              (noCountryTemplate ? "Нет шаблона этой страны, выберите вручную" : "Выберите шаблон")}
          </span>
          <ChevronDown size={16} aria-hidden="true" />
        </button>
        {templateDropdownOpen && (
          <div className="rec-pop res-template-pop">
            <input
              autoFocus
              type="text"
              className="rec-pop-search"
              placeholder="Поиск шаблона"
              aria-label="Поиск шаблона"
              value={templateSearch}
              onChange={(e) => setTemplateSearch(e.target.value)}
            />
            <div className="rec-pop-list" role="listbox" aria-label="Шаблоны">
              {filtered.length > 0 ? (
                filtered.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    role="option"
                    aria-selected={String(t.id) === String(selectedTemplateId)}
                    className="rec-pop-option"
                    onClick={() => {
                      pickedTemplateId.current = String(t.id);
                      setSelectedTemplateId(String(t.id));
                      setTemplateDropdownOpen(false);
                      setTemplateSearch("");
                    }}
                  >
                    <span>
                      {t.name}
                      <span className="rec-pop-code">
                        {" "}
                        · {t.isPair ? "один файл" : t.isPublic ? "общий" : t.isUser ? "мой" : "системный"}
                      </span>
                    </span>
                    {t.country && <span className="rec-pop-code">{t.country}</span>}
                  </button>
                ))
              ) : (
                <p className="rec-pop-option" style={{ cursor: "default" }}>
                  Шаблонов не найдено
                </p>
              )}
            </div>
            <Link
              to="/templates"
              className="px-menu-item"
              style={{ borderTop: "1px solid var(--line)", borderRadius: 0 }}
              onClick={() => setTemplateDropdownOpen(false)}
            >
              <FileText size={16} aria-hidden="true" /> Управление шаблонами
            </Link>
          </div>
        )}
      </div>
    );
  };

  const renderToolbar = () => (
    <>
      <PageHeader
        onBack={() => {
          if (id) {
            navigate("/history");
          } else {
            setSelectedRecordId(null);
            clearEditing();
          }
        }}
        backLabel={id ? "Назад к истории" : "Назад к загрузке"}
        leading={
          <span className={`res-type-tile tone-${resultMeta.tone}`} aria-hidden="true">
            <ResultIcon size={26} />
          </span>
        }
        title={resultTitle}
        subtitle={resultSubtitle}
      >
        <button
          type="button"
          className="px-icon-btn"
          aria-label="Удалить документ"
          onClick={handleDelete}
        >
          <Trash2 size={20} aria-hidden="true" />
        </button>
        {!(currentDocType === "contract" &&
          editableData?.translation_mode === "retypeset") && (
          <button
            type="button"
            className="px-btn px-btn-outline"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Сохранение…" : "Сохранить"}
          </button>
        )}
      </PageHeader>

      {currentDocType !== "contract" && (
        <DownloadBar>
          {renderTemplatePicker()}
          <Segmented
            label="Формат"
            value={downloadFormat}
            onChange={setDownloadFormat}
            options={[
              { key: "docx", label: "DOCX · перевод" },
              { key: "complex_pdf", label: "PDF · оригинал + перевод" },
            ]}
          />
          <button
            type="button"
            className="px-btn px-btn-sky res-download-btn"
            onClick={handleFillTemplate}
            disabled={!selectedTemplateId || fillingTemplate}
          >
            <Download size={20} aria-hidden="true" />
            {fillingTemplate ? "Готовим…" : "Скачать"}
          </button>
          {templatesError && (
            <p className="res-download-error" role="alert">
              {templatesError}
            </p>
          )}
        </DownloadBar>
      )}
    </>
  );

  const renderFieldEditor = () => {
    if (currentDocType === "contract") {
      return (
        <ContractResultView
          data={editableData}
          recordId={selectedRecordId}
          onTranslationChange={handleTranslationChange}
        />
      );
    }

    if (!editableData || !fieldSchema) return null;

    return (
      <div className="res-layout">
        <ScanPreview recordId={selectedRecordId} />
        <div className="res-fields">
        <StatusFilterBar
          stats={computeFieldStatusStats(editableData, fieldSchema)}
          value={statusFilter}
          onChange={setStatusFilter}
        />

        {Object.entries(fieldSchema).map(([catKey, catSchema]) => {
          const PASSPORT_CATEGORIES = [
            "biographical_page",
            "mrz",
            "visas",
            "stamps",
            "registration_stamps",
          ];
          const DL_CATEGORIES = ["dl_main", "dl_categories"];

          if (currentDocType === "passport" && DL_CATEGORIES.includes(catKey))
            return null;
          if (
            currentDocType === "drivers_license" &&
            PASSPORT_CATEGORIES.includes(catKey)
          )
            return null;

          const catData = editableData[catKey];
          const transData = editableData[`${catKey}_translation`];
          if (!catData) return null;
          if (Array.isArray(catData) && catData.length === 0) return null;
          // заверение/апостиль, которых нет в документе, не показываем пустыми формами
          if (
            isCivilDocType(currentDocType) &&
            (catKey === "notary" || catKey === "apostille") &&
            ![catData, transData].some((c) =>
              Object.values(c || {}).some((v) => v?.data),
            )
          )
            return null;

          if (statusFilter !== "all") {
            const { fields, is_array } = catSchema;
            const hasMatch =
              is_array && Array.isArray(catData)
                ? catData.some((item) =>
                  fields.some((f) => item[f.key]?.status === statusFilter),
                )
                : fields.some((f) => catData[f.key]?.status === statusFilter);
            if (!hasMatch) return null;
          }

          return (
            <DynamicCategorySection
              key={catKey}
              categoryKey={catKey}
              categorySchema={catSchema}
              data={catData}
              translatedData={transData}
              onFieldChange={handleFieldChange}
              statusFilter={statusFilter}
              preferTranslation={isCivilDocType(currentDocType)}
            />
          );
        })}

        <ManualEntrySection
          fieldSchema={fieldSchema}
          onAddItem={handleAddManualItem}
          docType={currentDocType}
        />
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="container">
        {error && (
          <div
            style={{
              position: "fixed",
              top: 80,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 9999,
              background: "var(--card)",
              border: "1px solid var(--destructive)",
              color: "var(--destructive)",
              padding: "0.75rem 1.5rem",
              borderRadius: 12,
              boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
              display: "flex",
              alignItems: "center",
              gap: "1rem",
              fontWeight: 600,
              fontSize: "0.9rem",
              maxWidth: 500,
            }}
          >
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              style={{
                background: "transparent",
                border: "none",
                color: "var(--destructive)",
                cursor: "pointer",
                fontSize: "1.2rem",
                padding: 0,
              }}
            >
              ×
            </button>
          </div>
        )}

        {duplicateError && (
          <div
            style={{
              position: "fixed",
              top: 80,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 9999,
              background: "var(--card)",
              border: "1px solid #3b82f6",
              color: "#60a5fa",
              padding: "0.75rem 1.5rem",
              borderRadius: 12,
              boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
              display: "flex",
              alignItems: "center",
              gap: "1rem",
              fontWeight: 600,
              fontSize: "0.9rem",
            }}
          >
            <FileText size={18} />
            <span>{duplicateError.message}</span>
            <button
              className="primary-btn"
              style={{
                padding: "0.4rem 0.8rem",
                fontSize: "0.8rem",
                background: "#3b82f6",
                boxShadow: "none",
              }}
              onClick={() => {
                const eid = duplicateError.existing_id;
                setDuplicateError(null);
                navigate(`/passport/${eid}`);
              }}
            >
              Перейти к результату
            </button>
            <button
              onClick={() => setDuplicateError(null)}
              style={{
                background: "transparent",
                border: "none",
                color: "#60a5fa",
                cursor: "pointer",
                fontSize: "1.2rem",
                padding: 0,
              }}
            >
              ×
            </button>
          </div>
        )}

        <div className="main-layout">
          <Modal
            isOpen={mergeModalOpen}
            onClose={() => {
              if (!processing) {
                setMergeModalOpen(false);
                setMergeQueue([]);
                setMergeSuccess(null);
              }
            }}
            title="Объединить фото в PDF"
          >
            {!mergeSuccess ? (
              <div className="mrg">
                <label
                  className="rec-drop"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    setMergeQueue((p) => [...p, ...Array.from(e.dataTransfer.files)]);
                  }}
                >
                  <span className="rec-drop-icon">
                    <Upload size={26} aria-hidden="true" />
                  </span>
                  <span className="rec-drop-title">
                    Перетащите фото или <u>выберите файлы</u>
                  </span>
                  <span className="rec-drop-sub">
                    PNG, JPG или PDF — страницы встанут в порядке списка
                  </span>
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png"
                    hidden
                    onChange={(e) => {
                      setMergeQueue((p) => [...p, ...Array.from(e.target.files)]);
                      e.target.value = "";
                    }}
                  />
                </label>

                {mergeQueue.length > 0 && (
                  <ol className="rec-files mrg-list">
                    {mergeQueue.map((f, i) => (
                      <li key={`${f.name}-${i}`} className="rec-file">
                        <span className="px-doc-icon" style={{ width: 40, height: 40, fontSize: 14, fontWeight: 800 }}>
                          {i + 1}
                        </span>
                        <span className="rec-file-text">
                          <span className="rec-file-name">{f.name}</span>
                        </span>
                        <button
                          type="button"
                          className="px-icon-btn is-small rec-file-remove"
                          aria-label={`Убрать ${f.name}`}
                          onClick={() => setMergeQueue((p) => p.filter((_, j) => j !== i))}
                        >
                          <X size={18} aria-hidden="true" />
                        </button>
                      </li>
                    ))}
                  </ol>
                )}

                <div className="mrg-actions">
                  <button
                    type="button"
                    className="px-btn px-btn-outline"
                    onClick={() => setMergeModalOpen(false)}
                  >
                    Отмена
                  </button>
                  <button
                    type="button"
                    className="px-btn px-btn-ink"
                    disabled={mergeQueue.length < 2 || processing}
                    onClick={() => handleMerge(mergeQueue)}
                  >
                    <Layers size={18} aria-hidden="true" />
                    {processing ? "Объединяем…" : `Объединить ${mergeQueue.length || ""}`.trim()}
                  </button>
                </div>
              </div>
            ) : (
              <div className="mrg mrg-done">
                <span className="mrg-done-icon">
                  <FileText size={30} aria-hidden="true" />
                </span>
                <h3 className="px-section-title" style={{ fontSize: 22 }}>
                  PDF готов
                </h3>
                <p className="px-page-sub" style={{ margin: 0 }}>
                  {mergeSuccess.name} · {(mergeSuccess.size / 1024 / 1024).toFixed(2).replace(".", ",")} МБ
                </p>
                <div className="mrg-actions">
                  <button
                    type="button"
                    className="px-btn px-btn-outline"
                    onClick={() => {
                      const u = URL.createObjectURL(mergeSuccess);
                      const a = document.createElement("a");
                      a.href = u;
                      a.download = mergeSuccess.name;
                      a.click();
                      URL.revokeObjectURL(u);
                    }}
                  >
                    <Download size={18} aria-hidden="true" /> Скачать PDF
                  </button>
                  <button type="button" className="px-btn px-btn-ink" onClick={finalizeMerge}>
                    Распознать этот PDF
                  </button>
                </div>
              </div>
            )}
          </Modal>

          {!selectedRecordId ? (
            processing && fileStatuses.length > 0 ? (
              <>
                <PageHeader title="Распознавание" subtitle={docTypeMeta?.label} />
                <ProcessingScreen
                  fileStatuses={fileStatuses}
                  progress={uploadProgress}
                  message={statusMessage}
                />
              </>
            ) : (
              <>
                <PageHeader
                  title="Распознавание"
                  subtitle="Выберите тип документа и загрузите файл"
                />
                <UploadScreen
                  documentType={documentType}
                  onTypeChange={changeDocumentType}
                  fileList={fileList}
                  isDragging={isDragging}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                  onFileInput={handleFileChange}
                  onRemoveFile={removeFileAt}
                  onOpenMerge={() => setMergeModalOpen(true)}
                  params={renderUploadOptions()}
                  summary={{
                    user,
                    pageCountInfo,
                    pageCountLoading,
                    canSubmit,
                    processing,
                    onSubmit: handleUpload,
                  }}
                  recent={processedFiles}
                  onRefreshRecent={fetchLastResults}
                />
              </>
            )
          ) : detailLoading ? (
            <div className="empty-state">
              <div
                className="empty-state-content"
                style={{ textAlign: "center" }}
              >
                <div
                  className="processing-spinner"
                  style={{ margin: "0 auto 1rem" }}
                />
                <p>Загрузка данных...</p>
              </div>
            </div>
          ) : (
            <div className="editor-layout">
              {renderToolbar()}
              {renderFieldEditor()}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// Страницы без оболочки: вход, регистрация и полноэкранный редактор шаблонов
const BARE_PATHS = ["/login", "/register", "/template-editor"];

const AppRoutes = () => (
  <Routes>
    <Route path="/login" element={<LoginPage />} />
    <Route path="/register" element={<RegisterPage />} />
    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
    <Route path="/reset-password" element={<ResetPasswordPage />} />
    <Route
      path="/ai-hints"
      element={
        <ProtectedRoute adminOnly>
          <AIPromptHintsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/field-schema"
      element={
        <ProtectedRoute adminOnly>
          <FieldSchemaPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/admin"
      element={
        <ProtectedRoute adminOnly>
          <AdminDashboard />
        </ProtectedRoute>
      }
    />
    <Route
      path="/admin/companies/:id"
      element={
        <ProtectedRoute adminOnly>
          <AdminCompanyAnalytics />
        </ProtectedRoute>
      }
    />
    <Route
      path="/"
      element={
        <ProtectedRoute>
          <UserDashboard />
        </ProtectedRoute>
      }
    />
    <Route
      path="/recognition"
      element={
        <ProtectedRoute>
          <MainDashboard />
        </ProtectedRoute>
      }
    />
    <Route
      path="/passport/:id"
      element={
        <ProtectedRoute>
          <MainDashboard />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dl/:id"
      element={
        <ProtectedRoute>
          <MainDashboard />
        </ProtectedRoute>
      }
    />
    <Route
      path="/doc/:id"
      element={
        <ProtectedRoute>
          <MainDashboard />
        </ProtectedRoute>
      }
    />
    <Route
      path="/contract/:id"
      element={
        <ProtectedRoute>
          <MainDashboard />
        </ProtectedRoute>
      }
    />
    <Route
      path="/history"
      element={
        <ProtectedRoute>
          <HistoryPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/templates"
      element={
        <ProtectedRoute>
          <TemplatesPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/translation-rules"
      element={
        <ProtectedRoute>
          <TranslationRulesPage />
        </ProtectedRoute>
      }
    />
    <Route path="/template-editor" element={<TemplateEditor />} />
    <Route path="*" element={<Navigate to="/" />} />
  </Routes>
);

const AppLayout = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();

  if (!user || BARE_PATHS.includes(pathname)) {
    return (
      <div className="app">
        <main className="content">
          <AppRoutes />
        </main>
      </div>
    );
  }
  return (
    <AppShell>
      <AppRoutes />
    </AppShell>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppLayout />
      </AuthProvider>
    </Router>
  );
}

export default App;
